type ChannelTalkUserContext = {
  userId?: string | number | null;
  username?: string | null;
  memberId?: string | null;
  memberHash?: string | null;
  memberHashConfigured?: boolean | null;
  service?: string | null;
  plan?: string | null;
  region?: string | null;
  appliedSolution?: string | null;
  businessStage?: string | null;
  journeyPhase?: string | null;
  aiLiteracy?: string | null;
  role?: string | null;
  tags?: string[];
  profile?: Record<string, string | number | boolean | null | undefined>;
};

type ChannelTalkRuntimeContext = {
  pluginKey?: string;
  enabled?: string;
  surface?: 'public' | 'authenticated';
  serviceId?: string;
  serviceName?: string;
  serviceGroup?: string;
  workspace?: string;
  serviceType?: string;
  environment?: string;
};

declare global {
  interface Window {
    STAR_T_CHANNEL_CONTEXT?: ChannelTalkRuntimeContext;
    STAR_T_CHANNEL_USER_CONTEXT?: ChannelTalkUserContext;
    StarTChannelIO?: {
      updateUserContext?: (userContext?: ChannelTalkUserContext) => void;
      updateContext?: (context?: Record<string, string>) => void;
      setPage?: () => void;
    };
    __AI_MARKETING_CHANNEL_SCRIPT_LOADING__?: Promise<void>;
  }
}

const normalizeEnvValue = (value: string | undefined): string => {
  const normalized = (value || '').replace(/\\r|\\n|\r|\n/g, '').trim();
  return ['""', "''"].includes(normalized) ? '' : normalized;
};

const normalizeEnabled = (value: string | undefined, fallback = 'false'): string => {
  const normalized = (normalizeEnvValue(value) || normalizeEnvValue(fallback)).toLowerCase();
  return ['true', '1', 'yes', 'on'].includes(normalized) ? 'true' : 'false';
};

const DEFAULT_AUTHENTICATED_PLUGIN_KEY = '70dfebc5-3706-4bda-b694-8ee2d7f23dbc';

const resolveEnabledForSurface = (surface: 'public' | 'authenticated'): string => {
  const bootVerified = normalizeEnabled(process.env.REACT_APP_CHANNEL_IO_BOOT_VERIFIED, 'false');
  if (bootVerified !== 'true') {
    return 'false';
  }

  const sharedEnabled = normalizeEnvValue(process.env.REACT_APP_CHANNEL_IO_ENABLED);
  if (surface === 'authenticated') {
    return normalizeEnabled(process.env.REACT_APP_CHANNEL_IO_AUTH_ENABLED, sharedEnabled || 'true');
  }
  return normalizeEnabled(process.env.REACT_APP_CHANNEL_IO_PUBLIC_ENABLED, 'false');
};

const shouldRequireMemberHash = (): boolean =>
  normalizeEnabled(process.env.REACT_APP_CHANNEL_IO_REQUIRE_MEMBER_HASH, 'true') === 'true';

const sanitizeUserContextForRuntime = (userContext?: ChannelTalkUserContext): ChannelTalkUserContext | undefined => {
  if (!userContext) return userContext;
  if (shouldRequireMemberHash()) return userContext;
  return {
    ...userContext,
    memberId: null,
    memberHash: null,
    memberHashConfigured: false,
  };
};

const resolvePluginKeyForSurface = (surface: 'public' | 'authenticated'): string => {
  const configured = normalizeEnvValue(process.env.REACT_APP_CHANNEL_IO_PLUGIN_KEY);
  if (configured) return configured;
  return surface === 'authenticated' ? DEFAULT_AUTHENTICATED_PLUGIN_KEY : '';
};

const buildRuntimeContext = (surface: 'public' | 'authenticated' = 'public'): ChannelTalkRuntimeContext => ({
  pluginKey: resolvePluginKeyForSurface(surface),
  enabled: resolveEnabledForSurface(surface),
  surface,
  serviceId: normalizeEnvValue(process.env.REACT_APP_CHANNEL_IO_SERVICE_ID) || 'ai_marketing_web',
  serviceName: normalizeEnvValue(process.env.REACT_APP_CHANNEL_IO_SERVICE_NAME) || 'AI Marketing',
  serviceGroup: normalizeEnvValue(process.env.REACT_APP_CHANNEL_IO_SERVICE_GROUP) || 'ain_business_os',
  workspace: normalizeEnvValue(process.env.REACT_APP_CHANNEL_IO_WORKSPACE) || 'ain_ai_strategy',
  serviceType: normalizeEnvValue(process.env.REACT_APP_CHANNEL_IO_SERVICE_TYPE) || 'marketing_os',
  environment: normalizeEnvValue(process.env.REACT_APP_ENV) || process.env.NODE_ENV || 'production',
});

const syncRuntimeContext = (
  surface: 'public' | 'authenticated' = 'public',
  userContext?: ChannelTalkUserContext,
): ChannelTalkRuntimeContext | null => {
  if (typeof window === 'undefined') return null;
  const runtimeContext = buildRuntimeContext(surface);
  window.STAR_T_CHANNEL_CONTEXT = {
    ...(window.STAR_T_CHANNEL_CONTEXT || {}),
    ...runtimeContext,
  };
  if (userContext) {
    window.STAR_T_CHANNEL_USER_CONTEXT = {
      ...(window.STAR_T_CHANNEL_USER_CONTEXT || {}),
      ...userContext,
    };
  }
  window.StarTChannelIO?.updateContext?.(runtimeContext as Record<string, string>);
  return runtimeContext;
};

const canBootChannelTalk = (
  surface: 'public' | 'authenticated',
  runtimeContext: ChannelTalkRuntimeContext | null,
  userContext?: ChannelTalkUserContext,
): boolean => {
  if (!runtimeContext?.pluginKey || runtimeContext.enabled !== 'true') return false;
  if (surface === 'authenticated' && shouldRequireMemberHash()) {
    return Boolean(userContext?.memberId && userContext?.memberHash);
  }
  return true;
};

const loadChannelTalk = (surface: 'public' | 'authenticated', userContext?: ChannelTalkUserContext): Promise<void> => {
  if (typeof window === 'undefined') return Promise.resolve();
  const runtimeUserContext = sanitizeUserContextForRuntime(userContext);
  const runtimeContext = syncRuntimeContext(surface, runtimeUserContext);
  if (!canBootChannelTalk(surface, runtimeContext, runtimeUserContext || window.STAR_T_CHANNEL_USER_CONTEXT)) {
    return Promise.resolve();
  }
  if (window.StarTChannelIO) return Promise.resolve();
  if (window.__AI_MARKETING_CHANNEL_SCRIPT_LOADING__) {
    return window.__AI_MARKETING_CHANNEL_SCRIPT_LOADING__;
  }

  window.__AI_MARKETING_CHANNEL_SCRIPT_LOADING__ = new Promise((resolve) => {
    const existing = document.querySelector<HTMLScriptElement>('script[data-ai-marketing-channelio="true"]');
    if (existing) {
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', () => resolve(), { once: true });
      return;
    }

    const script = document.createElement('script');
    script.src = '/channelio.js';
    script.async = true;
    script.dataset.aiMarketingChannelio = 'true';
    script.onload = () => resolve();
    script.onerror = () => resolve();
    document.body.appendChild(script);
  });

  return window.__AI_MARKETING_CHANNEL_SCRIPT_LOADING__;
};

export const loadChannelTalkForAuthenticatedUser = (userContext?: ChannelTalkUserContext): Promise<void> =>
  loadChannelTalk('authenticated', userContext);

export const loadChannelTalkForPublicPage = (): Promise<void> => loadChannelTalk('public');

export const updateChannelTalkUserContext = (userContext: ChannelTalkUserContext): void => {
  if (typeof window === 'undefined') return;
  const runtimeUserContext = sanitizeUserContextForRuntime(userContext) || userContext;
  window.STAR_T_CHANNEL_USER_CONTEXT = {
    ...(window.STAR_T_CHANNEL_USER_CONTEXT || {}),
    ...runtimeUserContext,
  };
  window.StarTChannelIO?.updateUserContext?.({
    userId: runtimeUserContext.userId,
    username: runtimeUserContext.username,
    memberId: runtimeUserContext.memberId,
    memberHash: runtimeUserContext.memberHash,
    memberHashConfigured: runtimeUserContext.memberHashConfigured,
    service: runtimeUserContext.service,
    plan: runtimeUserContext.plan,
    region: runtimeUserContext.region,
    appliedSolution: runtimeUserContext.appliedSolution,
    businessStage: runtimeUserContext.businessStage,
    journeyPhase: runtimeUserContext.journeyPhase,
    aiLiteracy: runtimeUserContext.aiLiteracy,
    role: runtimeUserContext.role,
    tags: runtimeUserContext.tags,
    profile: runtimeUserContext.profile,
  });
};

export const refreshChannelTalkPage = (): void => {
  if (typeof window === 'undefined') return;
  window.StarTChannelIO?.setPage?.();
};
